import Close16 from '@carbon/icons-react/es/close/16';
import Partnership16 from '@carbon/icons-react/es/partnership/16';
import UserMultiple20 from '@carbon/icons-react/es/user--multiple/20';
import { Tooltip } from 'antd';
import React, { FC, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Company, ProjectId, UserType } from 'api/models';
import { Project } from 'api/project';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { confirm } from 'components/atoms/modal';
import { Option } from 'components/atoms/select';
import { Page } from 'components/molecules/page';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { Sticky } from 'components/molecules/sticky';
import { toastService } from 'components/molecules/toast';
import { LogoContainer } from 'components/organisms/projects/project-create/form/team-details/logo-container';
import { getProjectById } from 'data/projects/actions';
import { useAccount } from 'hooks/use-account';
import { useGetAllMembersInProject } from 'hooks/use-idb';
import { useMemberships } from 'hooks/use-memberships';
import { useTranslation, useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUserProjectPermissions } from 'hooks/use-user-project-permissions';
import { useProjectMembersMetaQuery } from 'queries';
import { useGetAllActivePartnerCompanies } from 'queries/partner';
import {
  useAddPartnersToProjectMutation,
  useRemovePartnersToProjectMutation,
} from 'queries/project';
// eslint-disable-next-line import/no-internal-modules
import { PartnerSelect } from 'routes/projects/create/partner-select';
import { Memberships } from 'stores/memberships';
import { byLabel } from 'utils/comparators/by-label';
import { InviteUserInputV2 } from '../../global-invite/input-v2';
import { TeamTable } from './team-table';

// ========= STYLES ========== //

const PageContent = styled(Page.Content)`
  padding: var(--layout-03) var(--layout-03) 0;
  min-height: 100%;

  > .ant-row {
    padding: var(--spacing-03);
  }
`;

const Container = styled.div`
  margin-bottom: var(--spacing-12);
`;

const TeamDetails = styled.div`
  margin-bottom: var(--spacing-08);
`;

const MainContainer = styled.div`
  width: calc(var(--spacing-11) * 9);
  margin-top: var(--spacing-07);
  max-width: 100%;
`;
const Row = styled.div`
  display: flex;
`;
const TopRow = styled(Row)<{ $showPartnerButton?: boolean }>`
  position: sticky;
  top: 90px;
  z-index: 1;
  flex-direction: column;

  ${({ $showPartnerButton }) =>
    !$showPartnerButton
      ? css`
          .ant-form-item {
            padding-bottom: 0px;
          }
        `
      : ''};

  &:before {
    content: '';
    width: 100%;
    position: absolute;
    background: var(--white-100);
    top: -32px;
    height: ${({ $showPartnerButton }) =>
      $showPartnerButton ? '208px' : '168px'};
    z-index: 0;
  }
`;

const RemoveButton = styled(Button)`
  height: 24px;
  width: 24px;
  min-width: 0px;
  border-radius: 50%;
  &.ant-btn-loading {
    color: var(--blue-60);
  }
`;

const CompanyInfo = styled.div<{ $alwaysShowButton: boolean }>`
  padding: var(--spacing-04) var(--spacing-05);
  background: var(--coolest-gray-10);
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${RemoveButton} {
    ${({ $alwaysShowButton }) =>
      $alwaysShowButton ? '' : 'visibility: hidden;'};
  }
  &:hover ${RemoveButton} {
    visibility: visible;
  }
`;

export type MemberType = {
  type: string;
  userType: UserType;
  company: Company;
};
const TableSortOrder = [UserType.Native, UserType.External, UserType.Partner];

export const TeamMembers: FC<{
  project: Project;
}> = ({ project }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  // ========= HOOKS ========== //
  const { company } = useAccount();
  const { user } = useUser();
  const projectDetails = project;
  const { projectId } = projectDetails;
  const { data: membersMeta } = useProjectMembersMetaQuery(
    ProjectId(projectId),
  );

  const [status, memberships = Memberships.empty()] = useMemberships(
    ProjectId(projectId),
  );

  const projectMembers = useGetAllMembersInProject({
    projectId,
    isPrivate: false,
  });

  useDeepCompareEffect(() => {
    dispatch(getProjectById({ projectId }));
  }, [memberships]);

  const loggedInUserPermissions = useUserProjectPermissions(projectId);
  // ========== CONFIG ========== //
  const memberTypes: MemberType[] = useMemo(
    () =>
      [
        {
          type: 'nativeMembers',
          userType: UserType.Native,
          company,
        },
        {
          type: 'externalMembers',
          userType: UserType.External,
          company: projectDetails?.customer,
        },
      ].concat(
        projectDetails?.partners.map((partner) => ({
          type: `partner_${partner.companyId}`,
          userType: UserType.Partner,
          company: partner,
        })),
      ),
    [company, projectDetails?.customer, projectDetails?.partners],
  );
  const sortInvitationsByLogin = useCallback(
    (_memberTypes: MemberType[]) =>
      _memberTypes.sort((a, b) => {
        if (a.company.companyId === user.company.companyId) {
          return -1;
        }

        return (
          TableSortOrder.indexOf(a.userType) -
          TableSortOrder.indexOf(b.userType)
        );
      }),
    [user.company.companyId],
  );

  const title = useTranslation('team_members');
  const { data: partnerCompanies, isLoading: partnerCompaniesLoading } =
    useGetAllActivePartnerCompanies();
  const addedPartnerIds = useMemo(
    () => projectDetails.partners.map(({ companyId }) => companyId),
    [projectDetails.partners],
  );
  const partnerOptions: Option[] = useMemo(
    () =>
      (partnerCompanies ?? [])
        .filter(({ companyId }) => !addedPartnerIds.includes(companyId))
        .map(({ companyName, companyId, companyLogoUrl }) => ({
          label: companyName,
          value: companyId,
          imageUrl: companyLogoUrl,
        }))
        .sort(byLabel),
    [addedPartnerIds, partnerCompanies],
  );
  const { mutate: addPartners, isLoading: partnerAdditionInprogress } =
    useAddPartnersToProjectMutation();
  const onPartnerSelect = useCallback(
    (partnerCompanyOptions) => {
      addPartners({
        projectId: projectDetails.projectId,
        partners: partnerCompanyOptions.map(({ value }) => value),
      });
    },
    [addPartners, projectDetails.projectId],
  );
  const {
    mutate: removePartners,
    isLoading: isRemovingPartner,
    variables,
  } = useRemovePartnersToProjectMutation();
  const translate = useTranslate();
  const removePartner = useCallback(
    ({ companyName, companyId }: Company) => {
      const dialogTitle = translate(
        'project.invitation.remove_partner.confirmation.title',
        { companyName },
      );
      const content = translate(
        'project.invitation.remove_partner.confirmation.content.main',
        {
          companyName,
          projectName: projectDetails.projectName,
          strong: (chunks) => <strong>{chunks}</strong>,
        },
      );
      const okButtonText = translate('remove');

      confirm({
        title: dialogTitle,
        content,
        okButtonProps: {
          danger: true,
        },
        okText: okButtonText,
        onOk: () => {
          removePartners(
            {
              projectId: projectDetails.projectId,
              partners: [companyId],
            },
            {
              onSuccess: () => {
                toastService({
                  title: translate(
                    'project.invitation.remove_partner.success.title',
                    {
                      companyName,
                      b: (chunks) => <strong>{chunks}</strong>,
                    },
                  ),
                  message: '',
                }).success();
              },
              onError: () => {
                toastService({
                  title: translate(
                    'project.invitation.remove_partner.error.title',
                    {
                      companyName,
                      b: (chunks) => <strong>{chunks}</strong>,
                    },
                  ),
                  message: '',
                }).error();
              },
            },
          );
        },
      });
    },
    [
      translate,
      projectDetails.projectName,
      projectDetails.projectId,
      removePartners,
    ],
  );
  const canAddPartners = user.userType === UserType.Native;
  const showAddPartnersButton =
    canAddPartners && (partnerCompanies ?? []).length !== 0;

  return (
    <Page>
      <Helmet>{title}</Helmet>
      <Sticky top={0}>
        <Page.Header
          $white
          title={
            <HeaderWithIconTitle icon={<UserMultiple20 />} title={title} />
          }
        />
      </Sticky>
      <PageContent>
        <Container>
          <Form
            layout="vertical"
            form={form}
            name="projectdetails"
            key="content"
          >
            <TopRow $showPartnerButton={showAddPartnersButton}>
              <MainContainer>
                {projectDetails ? (
                  <InviteUserInputV2 project={projectDetails} form={form} />
                ) : null}
              </MainContainer>
              {showAddPartnersButton ? (
                <PartnerSelect
                  options={partnerOptions}
                  onChange={onPartnerSelect}
                  triggerComponent={
                    <Button
                      type="tertiary"
                      icon={<Partnership16 />}
                      iconPlacement="right"
                      loading={
                        partnerCompaniesLoading || partnerAdditionInprogress
                      }
                    >
                      <FormattedMessage id="projects.create.team_details.add_partner" />
                    </Button>
                  }
                />
              ) : null}
            </TopRow>
            <Row>
              <MainContainer>
                {sortInvitationsByLogin(memberTypes).map(
                  (memberType: MemberType) => (
                    <div key={memberType.type}>
                      <CompanyInfo
                        $alwaysShowButton={
                          isRemovingPartner &&
                          variables?.partners[0] ===
                            memberType?.company?.companyId
                        }
                      >
                        <div>
                          <LogoContainer
                            src={memberType?.company?.companyLogoUrl}
                            name={memberType?.company?.companyName}
                            translationId="projects.create.team"
                            translationValues={{
                              company:
                                memberType?.company?.companyName || 'Client',
                            }}
                            showPartnerTag={
                              memberType.userType === UserType.Partner &&
                              (user.userType === UserType.Partner
                                ? user.company.companyId !==
                                  memberType.company.companyId
                                : true)
                            }
                          />
                        </div>
                        {memberType.userType === UserType.Partner &&
                        user.userType === UserType.Native ? (
                          <Tooltip
                            title={
                              <FormattedMessage id="project.invitation.remove_partner.tooltip" />
                            }
                            placement="left"
                          >
                            <RemoveButton
                              type="text"
                              size="small"
                              danger
                              loading={
                                isRemovingPartner &&
                                variables?.partners[0] ===
                                  memberType?.company?.companyId
                              }
                              icon={<Close16 />}
                              onClick={() => removePartner(memberType.company)}
                            />
                          </Tooltip>
                        ) : null}
                      </CompanyInfo>
                      <TeamDetails>
                        <TeamTable
                          project={projectDetails}
                          data={projectMembers?.filter(
                            (u) =>
                              u.company.companyId ===
                              memberType.company.companyId,
                          )}
                          memberships={memberships}
                          loggedInUserPermissions={loggedInUserPermissions}
                          membersMeta={membersMeta}
                          isLoading={status === 'loading'}
                          usersType={memberType.userType}
                        />
                      </TeamDetails>
                    </div>
                  ),
                )}
              </MainContainer>
            </Row>
          </Form>
        </Container>
      </PageContent>
    </Page>
  );
};
